import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Label } from 'recharts';
import { Select } from '@mantine/core';
import { useApiClient } from '../../api/ApiClientContext';
import styles from './MoodCard.module.scss';

interface ChartDataPoint {
  day: string;
  dayIndex: number;
  mood_value: number | null;
  mood_label: string;
  color: string;
}

const PERIOD_OPTIONS = [
  { value: '7', label: 'This week' },
  { value: '14', label: '2 weeks' },
  { value: '30', label: 'Month' },
];

const getMoodColor = (moodValue: number | null): string => {
  if (!moodValue) return '#E5E7EB';
  switch (moodValue) {
    case 1: return '#F4B2D8';
    case 2: return '#3b5bdb';
    case 3: return '#748ffc';
    case 4: return '#ff922b';
    case 5: return '#94d82d';
    default: return '#E5E7EB';
  }
};

interface MoodCardProps {
  stretchHeight?: boolean;
}

const MoodCard: React.FC<MoodCardProps> = ({ stretchHeight = false }) => {
  const apiClient = useApiClient();
  const [period, setPeriod] = useState('7');
  const days = parseInt(period, 10);

  const { data: moodHistory } = useQuery({
    queryKey: ['moodHistory', days],
    queryFn: async () => {
      const res = await apiClient.api.getMoodHistoryApiV1MoodHistoryGet({ days });
      return res?.data;
    },
  });

  const chartData = useMemo((): ChartDataPoint[] => {
    const entries = moodHistory?.entries || [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const entryMap = new Map<string, { mood_value: number | null; mood_label: string; is_skipped: boolean }>();
    for (const e of entries) {
      entryMap.set(e.mood_date, {
        mood_value: e.mood_value ?? null,
        mood_label: e.mood_label ?? '',
        is_skipped: e.is_skipped,
      });
    }

    const points: ChartDataPoint[] = [];
    const dayLabelsShort = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const dateStr = d.toISOString().split('T')[0];
      const entry = entryMap.get(dateStr);

      const isSkipped = entry?.is_skipped === true;
      const moodVal = (!isSkipped && entry?.mood_value) ? entry.mood_value : null;

      let label: string;
      if (days <= 7) {
        label = dayLabelsShort[d.getDay()];
      } else if (days <= 14) {
        label = `${d.getDate()}/${d.getMonth() + 1}`;
      } else {
        label = i % 3 === 0 ? `${d.getDate()}/${d.getMonth() + 1}` : '';
      }

      points.push({
        day: label,
        dayIndex: days - 1 - i,
        mood_value: moodVal,
        mood_label: entry?.mood_label || '',
        color: getMoodColor(moodVal),
      });
    }

    return points;
  }, [moodHistory, days]);

  const CustomDot = (props: any) => {
    const { cx, cy, payload } = props;
    if (!payload.mood_value) return null;
    return <circle cx={cx} cy={cy} r={6} fill={payload.color} />;
  };

  const xTicks = useMemo(() => {
    if (days <= 7) return chartData.map((_, i) => i);
    if (days <= 14) return chartData.filter((_, i) => i % 2 === 0).map((_, i) => i * 2);
    return chartData.filter((_, i) => i % 3 === 0).map((_, i) => i * 3);
  }, [chartData, days]);

  return (
    <div className={`${styles.moodCard} ${stretchHeight ? styles.stretchHeight : ''}`}>
      <div className={styles.header}>
        <h3 className={styles.title}>Mood</h3>
        <Select
          value={period}
          onChange={(val) => val && setPeriod(val)}
          data={PERIOD_OPTIONS}
          size="xs"
          w={140}
          styles={{ input: { border: 'none', fontWeight: 400, fontSize: '0.875rem' } }}
        />
      </div>

      <div className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height={220}>
          <ScatterChart
            data={chartData}
            margin={{ top: 0, right: 10, left: 10, bottom: 10 }}
          >
            <CartesianGrid
              horizontal
              vertical
              stroke="#E9ECEF"
              strokeDasharray="3 3"
              strokeWidth={1}
            />
            <XAxis
              dataKey="dayIndex"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#868e96' }}
              type="number"
              ticks={xTicks}
              tickFormatter={(value) => chartData[value]?.day ?? ''}
              interval={0}
            />
            <YAxis
              domain={[0, 6]}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#495057' }}
              ticks={[1, 2, 3, 4, 5]}
              tickCount={5}
              interval={0}
              width={35}
              allowDecimals={false}
            >
              <Label
                value="Mood level"
                angle={-90}
                position="insideLeft"
                style={{
                  textAnchor: 'middle',
                  fill: '#757575',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  lineHeight: '1rem',
                }}
                offset={-2}
              />
            </YAxis>
            <Scatter dataKey="mood_value" shape={<CustomDot />} />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default MoodCard;
